/**
 * Fetches GET /role-management/me/effective-access for RBAC-gated navigation and routes.
 * Module visibility comes from Role Management (role → application assignments), not hardcoded personas.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuthContext } from './AuthContext';
import { useOrganizationContext } from './OrganizationContext';
import {
  canAccessNavItem,
  filterNavigationItems,
  grantedApplicationNames,
  hasFullPlatformShellAccess,
  DEFAULT_HOME_ROUTE,
} from '../../config/navAccessMap';

import { getSaasApiBaseUrl } from '../../utils/saasApiBaseUrl';

const EffectiveAccessContext = createContext({
  role: 'member',
  customRoleNames: [],
  permissions: [],
  applications: [],
  subscription: null,
  loading: true,
  canAccessNavItem: () => false,
  canAccessApplication: () => false,
  hasPermission: () => false,
  filterNavigationItems: (items) => items,
  getDefaultRoute: () => DEFAULT_HOME_ROUTE,
  hasFullPlatformShellAccess: false,
});

export function EffectiveAccessProvider({ children }) {
  const { user, isAuthenticated } = useAuthContext();
  const { organizationId } = useOrganizationContext();
  const [state, setState] = useState({
    role: 'member',
    customRoleNames: [],
    permissions: [],
    applications: [],
    subscription: null,
    loading: true,
  });

  useEffect(() => {
    if (!isAuthenticated || !user || !organizationId) {
      setState({
        role: 'member',
        customRoleNames: [],
        permissions: [],
        applications: [],
        subscription: null,
        loading: false,
      });
      return;
    }

    let cancelled = false;
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (!token) {
      setState((prev) => ({ ...prev, loading: false }));
      return;
    }

    setState((prev) => ({ ...prev, loading: true }));
    const url = `${getSaasApiBaseUrl()}/role-management/me/effective-access?organization_id=${encodeURIComponent(organizationId)}`;

    fetch(url, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      credentials: 'include',
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled) return;
        setState({
          role: data?.role || 'member',
          customRoleNames: data?.custom_role_names || [],
          permissions: data?.permissions || [],
          applications: data?.applications || [],
          subscription: data?.subscription || null,
          loading: false,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setState((prev) => ({ ...prev, loading: false }));
        }
      });

    return () => {
      cancelled = true;
    };
    // Key on the user's identity, not the whole user object: RBAC depends on
    // who the user is and their org, not on unrelated profile fields (e.g.
    // mfa_method). Depending on the full object made every setUser(...) refetch
    // access, flipping `loading` and unmounting the routed page via AccessGuard
    // — which discarded in-progress local state such as the 2FA QR enrollment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id, organizationId]);

  const accessSnapshot = useMemo(
    () => ({
      role: state.role,
      applications: state.applications,
      permissions: state.permissions,
      subscription: state.subscription,
      loading: state.loading,
    }),
    [state.role, state.applications, state.permissions, state.subscription, state.loading]
  );

  const canAccessNavItemFn = useCallback(
    (itemId) => canAccessNavItem(itemId, accessSnapshot),
    [accessSnapshot]
  );

  const canAccessApplication = useCallback(
    (applicationName) => {
      if (state.loading) return false;
      if (hasFullPlatformShellAccess(accessSnapshot)) return true;
      const name = String(applicationName || '').toLowerCase();
      return (state.applications || []).some((a) => (a.name || '').toLowerCase() === name);
    },
    [state.loading, accessSnapshot, state.applications]
  );

  const hasPermission = useCallback(
    (permissionKey) => (state.permissions || []).includes(permissionKey),
    [state.permissions]
  );

  const filterNav = useCallback(
    (items) => filterNavigationItems(items, accessSnapshot),
    [accessSnapshot]
  );

  const getDefaultRoute = useCallback(() => DEFAULT_HOME_ROUTE, []);

  const value = useMemo(
    () => ({
      ...state,
      canAccessNavItem: canAccessNavItemFn,
      canAccessApplication,
      hasPermission,
      filterNavigationItems: filterNav,
      getDefaultRoute,
      hasFullPlatformShellAccess: hasFullPlatformShellAccess(accessSnapshot),
      grantedApplicationNames: () => grantedApplicationNames(accessSnapshot),
    }),
    [state, canAccessNavItemFn, canAccessApplication, hasPermission, filterNav, getDefaultRoute, accessSnapshot]
  );

  return (
    <EffectiveAccessContext.Provider value={value}>{children}</EffectiveAccessContext.Provider>
  );
}

export function useEffectiveAccess() {
  return useContext(EffectiveAccessContext);
}

export default EffectiveAccessContext;
